import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { useMediaTicket, mediaUrl } from "@/lib/mediaTicket";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SectionLoader, EmptyState } from "@/components/common";
import { Scissors, Download, Globe, Users2, Megaphone } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SocialPostDialog } from "@/components/social/SocialPostGenerator";

export default function SharedClips() {
  const ticket = useMediaTicket();
  const { can } = useAuth();
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postSource, setPostSource] = useState(null);

  useEffect(() => {
    api.get("/clips/shared").then((r) => setClips(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <SectionLoader />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Shared Clips</h1>
        <p className="mt-1 text-muted-foreground">Highlights from our conversations, shared by the Core team.</p>
      </div>

      {clips.length === 0 ? (
        <EmptyState icon={Scissors} title="No shared clips yet" description="When the Core team shares podcast highlights, they'll appear here." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {clips.map((c) => (
            <Card key={c.clip_id} className="border-border bg-card" data-testid="shared-clip-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">{c.title}</p>
                    {c.recording_title && <p className="truncate text-xs text-muted-foreground">from {c.recording_title}</p>}
                  </div>
                  <Badge variant="secondary" className="gap-1">{c.share_level === "public" ? <><Globe className="h-3 w-3" />Public</> : <><Users2 className="h-3 w-3" />Members</>}</Badge>
                </div>
                <audio controls className="mt-3 w-full" src={mediaUrl(`${API_BASE}/clips/${c.clip_id}/audio`, ticket) || undefined} data-testid="shared-clip-audio" />
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button asChild size="sm" variant="secondary"><a href={mediaUrl(`${API_BASE}/clips/${c.clip_id}/audio`, ticket) || "#"} download><Download className="mr-1.5 h-3.5 w-3.5" />Download</a></Button>
                  {can("producer_tools") && (
                    <Button size="sm" className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="shared-clip-createpost"
                      onClick={() => setPostSource({ type: "clip", id: c.clip_id, title: c.title, public_url: c.share_level === "public" && c.public_token ? `${window.location.origin}/clip/${c.public_token}` : null })}>
                      <Megaphone className="mr-1.5 h-3.5 w-3.5" />Create post
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
